import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { TooltipHeader } from './TooltipHeader';
import { TooltipVotes } from './TooltipVotes';
import { TooltipStats } from './TooltipStats';
import { TooltipLink } from './TooltipLink';
import { TooltipLoading } from './TooltipLoading';
import { getTranslation } from 'common/react/utils/translations';
import {
    useGetPlayerProfileQuery,
    useGetPlayerSeasonRoundStatsQuery
} from 'common/store/modules/api/statsPro-api';

/**
 * @param {object} props - Props.
 * @param {object} props.row - Row.
 * @param {object} props.cell - Cell.
 * @param {boolean} props.isPredictor - Cell.
 * @returns {JSX.Element} Component.
 */
export const TooltipContent = (props) => {
    const isPredictor = props.isPredictor ? true : false;
    const tooltipContentRef = useRef();

    const { data: player = {}, isFetching: playerIsFetching } =
        useGetPlayerProfileQuery({
            playerId: props.player,
            seasonId: props.seasonPid
        });

    const { data: rounds = {}, isFetching: roundsIsFetching } =
        useGetPlayerSeasonRoundStatsQuery([props.player, props.seasonPid]);

    const playerData = useMemo(() => {
        if (!Object.keys(player).length || !Object.keys(rounds).length) {
            return null;
        }

        return {
            player: player,
            rounds: {
                playerId: props.player,
                seasonId: props.seasonPid,
                roundStats: rounds[props.seasonPid]
            }
        };
    }, [player, rounds, props.player, props.seasonPid]);

    useEffect(() => {
        if (tooltipContentRef.current) {
            tooltipContentRef.current.focus();
        }
    }, [tooltipContentRef.current, playerData]);

    /**
     * Tracks the round tooltip as a view_modal/close_modal pair - mount/unmount
     * naturally covers both dismiss paths (click away or click another round)
     */
    useEffect(() => {
        /* eslint-disable camelcase */
        PULSE.app.tracking.recordEvent(
            PULSE.app.common.CONSTANTS.TRACKING.EVENTS.MODAL.VIEW,
            { eventData: { modal_name: 'player round predicted votes' } }
        );

        return () => {
            PULSE.app.tracking.recordEvent(
                PULSE.app.common.CONSTANTS.TRACKING.EVENTS.MODAL.CLOSE,
                { eventData: { modal_name: 'player round predicted votes' } }
            );
        };
        /* eslint-enable camelcase */
    }, []);

    const roundNumber = props?.cell?.column?.id?.replace('round', '');
    const roundMatches = props.row?.[props?.cell?.column?.id]?.matches ?? [];

    /**
     * For each match from the round, map the player stats.
     * If player stats don't exist (not played or just no data), creating a faux object so the tooltip won't crash
     */
    const matchesPerRound = useMemo(() => {
        if (playerData) {
            return roundMatches.length
                ? roundMatches.map((match) => {
                      return Object.assign(
                          {
                              utcStartTime: match.utcStartTime,
                              played: match.played
                          },
                          playerData?.rounds?.roundStats?.find(
                              (matchStats) =>
                                  matchStats.matchId === match.matchId
                          ) ?? {
                              matchId: match.matchId,
                              roundNumber: roundNumber,
                              opponent: {
                                  teamId: match.opponent.providerId,
                                  teamAbbr: match.opponent.abbreviation,
                                  teamName: match.opponent.name,
                                  teamNickname: match.opponent.nickname
                              }
                          }
                      );
                  })
                : playerData?.rounds?.roundStats?.filter(
                      (item) => item.roundNumber === parseInt(roundNumber)
                  ) ?? [];
        }

        return [];
    }, [roundMatches, playerData?.rounds]);

    // get player leaderboard data
    const playerLeaderboardData = props.leaderboardData?.leaderboard.find(
        (item) => item.player.playerId === props.player
    );

    return (
        <>
            {playerIsFetching || roundsIsFetching ? (
                <TooltipLoading />
            ) : (
                <div key={playerData.player.playerId} ref={tooltipContentRef}>
                    {playerData.player && (
                        <TooltipHeader player={playerData.player} />
                    )}
                    {isPredictor &&
                    playerData?.rounds?.roundStats?.filter(
                        (item) => item.roundNumber === parseInt(roundNumber)
                    )?.length === 0 ? (
                        <p className="player-stats-tooltip-message">
                            {getTranslation(
                                'label.playerAwards.tooltip.noData'
                            )}
                        </p>
                    ) : (
                        <>
                            <TooltipVotes
                                {...props}
                                player={playerData.player}
                                rounds={playerData.rounds}
                                matchesPerRound={matchesPerRound}
                                isPredictor={isPredictor}
                            />
                            <TooltipStats
                                {...props}
                                player={playerData.player}
                                rounds={playerData.rounds}
                                matchesPerRound={matchesPerRound}
                                roundByRoundVotes={
                                    playerLeaderboardData?.roundByRoundVotes
                                }
                            />
                        </>
                    )}
                    <TooltipLink
                        {...props}
                        player={playerData.player}
                        rounds={playerData.rounds}
                        matchesPerRound={matchesPerRound}
                    />
                </div>
            )}
        </>
    );
};

TooltipContent.propTypes = {
    row: PropTypes.object.isRequired,
    cell: PropTypes.object.isRequired,
    benchmark: PropTypes.string,
    seasonPid: PropTypes.string,
    player: PropTypes.string,
    leaderboardData: PropTypes.object,
    isPredictor: PropTypes.bool
};
